import { baseApi } from "@/services/api";
import type {
  ApiEmployeeRole,
  ArchiveEmployeeRoleResponse,
  RestoreEmployeeRoleResponse,
  CreateEmployeeRoleRequest,
  CreateEmployeeRoleResponse,
  ListEmployeeRolesResponse,
  UpdateEmployeeRoleRequest,
  UpdateEmployeeRoleResponse,
} from "./employeeRoleTypes";
export { resolveCatalogRoleId } from "./employeeRoleSeeds";

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

function unwrap<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || !raw.data) {
    throw new Error(raw?.message || fallbackMessage);
  }
  return raw.data;
}

export const employeeRolesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listEmployeeRoles: builder.query<
      ListEmployeeRolesResponse,
      { status?: "active" | "archived" } | void
    >({
      query: (params) => ({
        url: "/sa/employee-roles",
        method: "GET",
        params: params?.status ? { status: params.status } : undefined,
      }),
      transformResponse: (raw: ApiEnvelope<ListEmployeeRolesResponse>) =>
        unwrap(raw, "Failed to load employee roles"),
      providesTags: ["EmployeeRoles"],
    }),
    createEmployeeRole: builder.mutation<
      CreateEmployeeRoleResponse,
      CreateEmployeeRoleRequest
    >({
      query: (body) => ({
        url: "/sa/employee-roles",
        method: "POST",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<CreateEmployeeRoleResponse>) =>
        unwrap(raw, "Failed to create employee role"),
      invalidatesTags: ["EmployeeRoles"],
    }),
    updateEmployeeRole: builder.mutation<
      UpdateEmployeeRoleResponse,
      UpdateEmployeeRoleRequest
    >({
      query: ({ roleId, ...body }) => ({
        url: `/sa/employee-roles/${encodeURIComponent(roleId)}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (raw: ApiEnvelope<UpdateEmployeeRoleResponse>) =>
        unwrap(raw, "Failed to update employee role"),
      invalidatesTags: ["EmployeeRoles"],
    }),
    archiveEmployeeRole: builder.mutation<
      ArchiveEmployeeRoleResponse,
      string
    >({
      query: (roleId) => ({
        url: `/sa/employee-roles/${encodeURIComponent(roleId)}/archive`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<ArchiveEmployeeRoleResponse>) =>
        unwrap(raw, "Failed to archive employee role"),
      invalidatesTags: ["EmployeeRoles", "Employees"],
    }),
    restoreEmployeeRole: builder.mutation<
      RestoreEmployeeRoleResponse,
      string
    >({
      query: (roleId) => ({
        url: `/sa/employee-roles/${encodeURIComponent(roleId)}/restore`,
        method: "POST",
      }),
      transformResponse: (raw: ApiEnvelope<RestoreEmployeeRoleResponse>) =>
        unwrap(raw, "Failed to restore employee role"),
      invalidatesTags: ["EmployeeRoles", "Employees"],
    }),
  }),
});

export const {
  useListEmployeeRolesQuery,
  useCreateEmployeeRoleMutation,
  useUpdateEmployeeRoleMutation,
  useArchiveEmployeeRoleMutation,
  useRestoreEmployeeRoleMutation,
} = employeeRolesApi;

export function isActiveRole(role: ApiEmployeeRole): boolean {
  return role.status === "active";
}

export function activeRoleSelectOptions(
  roles: ApiEmployeeRole[],
  currentRoleKey?: string | null,
) {
  const active = roles.filter(isActiveRole);
  const options = [
    { value: "", label: "Select Role" },
    ...active.map((role) => ({
      value: role.roleKey,
      label: role.displayName,
    })),
  ];

  const current = (currentRoleKey || "").trim();
  if (current && !options.some((option) => option.value === current)) {
    const match = roles.find((role) => role.roleKey === current);
    options.push({
      value: current,
      label: match?.displayName || current,
    });
  }

  return options;
}

export function activeRoleDisplayNames(roles: ApiEmployeeRole[]): string[] {
  return roles
    .filter(isActiveRole)
    .sort(
      (a, b) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
        a.displayName.localeCompare(b.displayName),
    )
    .map((role) => role.displayName);
}

export function roleFilterOptions(
  roles: ApiEmployeeRole[],
  rowRoleLabels: string[],
): string[] {
  const fromCatalog = activeRoleDisplayNames(roles);
  const seen = new Set(fromCatalog);
  const extras: string[] = [];

  for (const label of rowRoleLabels) {
    const trimmed = (label || "").trim();
    if (!trimmed || trimmed === "—") continue;
    if (!seen.has(trimmed)) {
      seen.add(trimmed);
      extras.push(trimmed);
    }
  }

  extras.sort((a, b) => a.localeCompare(b));
  return [...fromCatalog, ...extras];
}
